import { Button, LoaderButton } from 'components/Button';
import Modal from 'components/Shared/Modal';
import { useEffect, useRef, useState } from 'react';
import styles from './styles.scss';
import { NO_ELEMENT, rm } from 'utils';
import {
  hasCorrectCookies,
  setNewsletterCookie,
  NewsletterModalCookie,
  hasSelectedShowLater
} from './utils';
import { Input } from 'components/Shared/Form/themes/BGH-RD3/input';
import { Bubble } from './components/Bubble';
import { getHoverContainer } from 'components/Pagelayout/PageLayout';
import Markdown from 'components/Shared/Markdown';
import { useDispatch, useSelector } from 'react-redux';
import {
  isMarketingCookieAccepted,
  isNewsletterPushActive,
  isNewsletterPushDiscountActive
} from 'components/Newsletter/selector';
import { IconCross } from 'components/Shared/Icon/themes/BGH-RD3/iconCross';
import { useNewsletterForm } from 'components/Newsletter/themes/BGH/useNewsletterForm';
import { isValidEmail } from 'components/Shared/Form';
import { StickyTag } from './components/StickyTag';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { IconCheckmark } from 'components/Shared/Icon';
import { GtmNewsletterModalDecline, GtmNewsletterModalLater } from 'store/googleTagManager/actions';

const USP_LIST_DELIMITER = '|';

const getNewsletterUsps = (): JSX.Element => {
  const uspList = rm('Newsletter_Push_Usps').split(USP_LIST_DELIMITER);

  return (
    <ul>
      {uspList.map((usp, index) => (
        <li className={styles.usp} key={index}>
          <IconCheckmark size="small" />
          <span>{usp.trim()}</span>
        </li>
      ))}
    </ul>
  );
};

interface NewsletterModalProps {
  userHasInteracted: boolean;
}

export const NewsletterModal = ({ userHasInteracted }: NewsletterModalProps) => {
  const isFeatureActive = useSelector(isNewsletterPushActive);
  const showDiscount = useSelector(isNewsletterPushDiscountActive);

  const [showModal, setShowModal] = useState(false);
  const [showStickyTag, setShowStickyTag] = useState(false);
  const acceptedMarketingCookie = useSelector(isMarketingCookieAccepted);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useSelector(isMobileSelector);
  const reduxDispatch = useDispatch();
  const {
    handleChange,
    subscribe,
    email,
    resetEmail,
    message,
    isFetching,
    showModal: showInfoModal,
    close: closeInfoModal,
    isComplete,
    isError
  } = useNewsletterForm('modal');

  useEffect(() => {
    const allowedToShow = acceptedMarketingCookie && hasCorrectCookies();
    const showStickyTag = hasSelectedShowLater();

    if (showStickyTag) {
      setShowStickyTag(allowedToShow);
    } else {
      setShowModal(allowedToShow);
    }
  }, [acceptedMarketingCookie]);

  useEffect(() => {
    if (isError && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [isError]);

  if (isFeatureActive === false) {
    return NO_ELEMENT;
  }

  const handleInfoModalClose = () => {
    setNewsletterCookie(NewsletterModalCookie.ACCEPTED);
    setShowModal(false);
    closeInfoModal();
  };

  const onLaterClick = () => {
    setNewsletterCookie(NewsletterModalCookie.LATER);
    reduxDispatch(GtmNewsletterModalLater());
    resetEmail();
    showOnlyTag();
  };

  const onDeclineClick = () => {
    setNewsletterCookie(NewsletterModalCookie.DECLINED);
    reduxDispatch(GtmNewsletterModalDecline());
    setShowModal(false);
  };

  const showOnlyModal = () => {
    setShowStickyTag(false);
    setShowModal(true);
  };

  const showOnlyTag = () => {
    setShowStickyTag(true);
    setShowModal(false);
  };

  if (showStickyTag) {
    return (
      <StickyTag isDiscountVariant={showDiscount} isMobile={isMobile} onClick={showOnlyModal} />
    );
  }

  return (
    <Modal
      show={showModal && userHasInteracted}
      contentClassName={styles.modal}
      containerSelector={getHoverContainer}
    >
      <Modal
        show={showInfoModal && isComplete}
        showDefaultButton={true}
        onClose={handleInfoModalClose}
      >
        <p>{message}</p>
      </Modal>

      <Bubble isDiscount={showDiscount} />

      <button id={styles.closeBtn} onClick={onDeclineClick}>
        <IconCross />
      </button>

      <div id={styles.body}>
        <h2>{rm('Newsletter_Push_Title')}</h2>

        {getNewsletterUsps()}

        {isError && <p className={styles.errorMsg}>{message}</p>}

        <Input
          className={styles.emailInput}
          type="email"
          innerRef={emailInputRef}
          onChange={handleChange}
          value={email}
          valid={isValidEmail(email)}
          invalid={!isValidEmail(email) && email !== ''}
          placeholder={rm('Newsletter_Push_Email_Input')}
        />

        <div className={styles.btnContainer}>
          <form onSubmit={subscribe}>
            <LoaderButton
              type="submit"
              className={styles.acceptBtn}
              loading={isFetching}
              disabled={!isValidEmail(email)}
            >
              {rm('Newsletter_Push_Accept_Button')}
            </LoaderButton>
          </form>
          <Button appearance="whitePill" onClick={onLaterClick}>
            {rm('Newsletter_Push_Later_Button')}
          </Button>
          <Button appearance="borderless" className={styles.declineBtn} onClick={onDeclineClick}>
            <IconCross />
            {rm('Newsletter_Push_Decline_Button')}
          </Button>
        </div>

        <Markdown
          className={styles.policyContainer}
          tag="p"
          content={rm('Newsletter_disclaimer')}
        />
      </div>
    </Modal>
  );
};
